import { ApplicationState } from "../../store";
import {
    getLanguageCode,
    getLanguageResourceGeneralStrings,
    getLanguageResourceCountryMappedStrings,
    getLanguageResourcePrefixOptions,
    getLanguageResourceCountrySelectOptions,
    getLanguageResourceAllRegionsSelectOptions,
    getLanguageResourceContactPointOptions,
    getLanguageResourcePhoneTypeOptions,
    getLanguageResourcePatientOverviewStrings
} from "../../store/LanguageLocale/selector";
import { getIsUserAuthenticated } from "../../store/Login/selectors";
import {
    getCurrentPatientInformation,
    getCurrentPatientInformationMode,
    getShowPatientInformationEditModal,
    getCurrentEditPopupSelected,
    getCurrentEditPopupCategoryIndex,
    getIsCurrentItemNew,
    getIsDatePickerVisibile,
    getPopupDeleteConfirmationVisibility
} from "../../store/PatientInformationForm/selectors";
import {
    getClinicCountry,
    getClinicRegion
} from "../../store/ClinicInformation/selectors";
import { getFocusedElementId } from "../../store/Keyboard/selectors";
import {
    PatientIndividualInformationDto,
    EmailCheckin,
    PhoneCheckin,
    AddressCheckin,
    DemographicsCheckin,
    HealthCardCheckin,
    TypeOfFinalTransaction,
    ClientEventScreen,
    PreferredPharmacyCheckin
} from "../../dtos/KioskDtos.dtos";
import { PATIENT_OVERVIEW_EDIT_WINDOW } from "../../store/PatientInformationForm/type";
import {
    disconnectKeyboard,
    connectInputToKeyboard
} from "../../store/Keyboard/actions";
import {
    togglePatientInformationEditPopup,
    saveEmailEditData,
    savePhoneEditData,
    saveAddressEditData,
    saveDemographicsEditData,
    savePreferredPharmacyEditData
} from "../../store/PatientInformationForm/actions";

import { Routes } from "../../constants/routes";
import { KeyboardType } from "../../constants/keyboardTypes";
import { connect } from "react-redux";
import { PatientOverview } from "./PatientOverview.component";
import { Dispatch } from "redux";
import {
    IPatientOverviewMapStateProps,
    IPatientOverviewMapDispatchProps
} from "./Types";
import { getLoadingSpinnerVisibilityState } from "../../store/LoadingSpinner/selectors";
import { getOptionAbleToSeeAndModifyExistingPatientData, getOptionAbleToSeeAndModifyExistingPreferredPharmacy } from "../../store/AdminOptions/selectors";
import { redirectToSurvey } from "../../sagas/surveyRedirectSaga";
import { push } from "../../store/Routing/actions";
import { getPatientAppointmentsFromServer } from "../../sagas/Appointments/actions";
import { putPatientInfoToServer } from "../../sagas/PatientInfo/actions";

const mapStateToProps = (state: ApplicationState): IPatientOverviewMapStateProps => {
    return {
        // Strings
        lang: getLanguageCode(state.localeText),
        text: getLanguageResourceGeneralStrings(state.localeText),
        isPatientInfoChanged: state.patientInformationForm.patientInfoChanged,
        isPersonalModificationOptionActive: getOptionAbleToSeeAndModifyExistingPatientData(state.adminOptions),
        isPreferredPharmacyOptionActive: getOptionAbleToSeeAndModifyExistingPreferredPharmacy(state.adminOptions),
        patientOverviewStrings: getLanguageResourcePatientOverviewStrings(state.localeText),
        countryList: getLanguageResourceCountrySelectOptions(state.localeText),
        stateList: getLanguageResourceAllRegionsSelectOptions(state.localeText),
        countryMappedStrings: getLanguageResourceCountryMappedStrings(state.localeText),
        isUserAuthenticated: getIsUserAuthenticated(state.login),
        emailContactPointOptions: getLanguageResourceContactPointOptions(state.localeText),
        patientInfo: getCurrentPatientInformation(state.patientInformationForm),
        phoneTypesOptions: getLanguageResourcePhoneTypeOptions(state.localeText),
        currentPatientInfoMode: getCurrentPatientInformationMode(state.patientInformationForm),
        prefixOptions: getLanguageResourcePrefixOptions(state.localeText),
        // Patient overview
        showPatientInfoEditModal: getShowPatientInformationEditModal(state.patientInformationForm),
        patientEditPopupSelected: getCurrentEditPopupSelected(state.patientInformationForm),
        patientEditPopupCategoryIndex: getCurrentEditPopupCategoryIndex(state.patientInformationForm),
        patientEditIsCurrentItemNew: getIsCurrentItemNew(state.patientInformationForm),
        showDatePicker: getIsDatePickerVisibile(state.patientInformationForm),

        defaultCountry: getClinicCountry(state.clinicInformation),
        defaultRegion: getClinicRegion(state.clinicInformation),

        patientEditDeletePopupVisibility: getPopupDeleteConfirmationVisibility(state.patientInformationForm),
        isKeyboardActive: state.keyboard.showKeyboard,
        keyboardFocusedName: getFocusedElementId(state.keyboard),
        isLoadingSpinnerActive: getLoadingSpinnerVisibilityState(state.loadingSpinner),
        myleClinicSettings: state.clinicInformation.myleClinicSettings
    };
}

const mapDispatchToProps = (dispatch: Dispatch): IPatientOverviewMapDispatchProps => ({
    putPatientInfoToServer: (patientInfo: PatientIndividualInformationDto) => dispatch(putPatientInfoToServer(patientInfo)),

    getAppointmentInfoFromServer: (patientId: string) => dispatch(getPatientAppointmentsFromServer(patientId)),

    showEditModal: (value: boolean, category?: PATIENT_OVERVIEW_EDIT_WINDOW, index?: number) => {
        dispatch(disconnectKeyboard());
        if (category as PATIENT_OVERVIEW_EDIT_WINDOW) {
            if (index || index === 0) {
                dispatch(togglePatientInformationEditPopup(value, category as PATIENT_OVERVIEW_EDIT_WINDOW, index))
            } else {
                dispatch(togglePatientInformationEditPopup(value, category as PATIENT_OVERVIEW_EDIT_WINDOW))
            }
        } else {
            dispatch(togglePatientInformationEditPopup(value))
        }
    },

    return: () => dispatch(redirectToSurvey(push(Routes.introduction), TypeOfFinalTransaction.CancelledOnTheirOwn, ClientEventScreen.PatientOverview)),

    saveEmails: (email: EmailCheckin[]) => {
        dispatch(saveEmailEditData(email));
    },

    savePhones: (phones: PhoneCheckin[]) => {
        dispatch(savePhoneEditData(phones));
    },

    saveAddress: (addresses: AddressCheckin[]) => {
        dispatch(saveAddressEditData(addresses));
    },

    saveDemographicsAndHealthCard: (demographics: DemographicsCheckin, healthCard: HealthCardCheckin) => {
        dispatch(disconnectKeyboard());
        dispatch(saveDemographicsEditData(demographics, healthCard, false));
    },

    savePreferredPharmacy: (pharmacy: PreferredPharmacyCheckin) => {
        // TODO: Implement frontend and backend logic to save preferred pharmacy
        dispatch(savePreferredPharmacyEditData(pharmacy));
    },

    showKeyboard: (pipe: (value: any) => void, value = "", mask: string = "", id: string) => dispatch(connectInputToKeyboard(pipe, value, mask, id, KeyboardType.STANDARD)),

    showPhoneKeyboard: (pipe: (value: any) => void, value = "", mask: string = "", id: string) => { dispatch(connectInputToKeyboard(pipe, value, mask, id, KeyboardType.NUMPAD)) },

    hideKeyboard: () => {
        dispatch(disconnectKeyboard());
    }
});

export default connect<IPatientOverviewMapStateProps, IPatientOverviewMapDispatchProps, void>(mapStateToProps, mapDispatchToProps)(PatientOverview);


// WEBPACK FOOTER //
// ./src/components/PatientOverview/PatientOverview.container.ts